// «Мои стейты» — list main_app `state`'ов привязанного main_app аккаунта
// + кнопка «Извлечь компоновку» (создаёт public.drillstring).
//
// Без линка main_app аккаунта список пустой; UI показывает баннер с указанием
// привязать аккаунт в Settings.

import { createSignal, createResource, For, Show } from 'solid-js'
import type { Component } from 'solid-js'
import { Icon } from './Icons'
import { listStates, extractState } from './api/states'
import type { PublicStateMeta } from './api/states'
import { useNavigate } from '@tanstack/solid-router'

const ru = new Intl.NumberFormat('ru-RU')

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(2)} MB`
}

function formatDate(s: string): string {
  // PublicStateMeta.ctime/mtime — RFC3339 с Z; toLocale делает читабельно.
  try {
    return new Date(s).toLocaleString('ru-RU', {
      year:   'numeric',
      month:  '2-digit',
      day:    '2-digit',
      hour:   '2-digit',
      minute: '2-digit',
    })
  } catch {
    return s
  }
}

type Toast =
  | { kind: 'ok'; text: string }
  | { kind: 'err'; text: string }

export const StatesPage: Component = () => {
  const navigate = useNavigate()
  const [items, { refetch }] = createResource<PublicStateMeta[]>(listStates)
  const [busyId, setBusyId] = createSignal<number | null>(null)
  const [toast, setToast] = createSignal<Toast | null>(null)

  const onExtract = async (s: PublicStateMeta) => {
    setBusyId(s.state_id)
    setToast(null)
    try {
      const r = await extractState(s.state_id)
      // Короткое summary с переходом на drillstring если хочется.
      const lines: string[] = [
        `Компоновка #${r.drillstring_id} создана: ${ru.format(r.sections_count)} секц.`,
      ]
      if (r.well_inaccessible) {
        lines.push('⚠ well из проекта вам сейчас недоступен — компоновка создана, но карточка рiга скрыта.')
      }
      setToast({ kind: 'ok', text: lines.join(' ') })
    } catch (e) {
      setToast({ kind: 'err', text: String(e) })
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div class="page page-wide">
      <div class="page-header">
        <div>
          <h1 class="page-title">Мои стейты</h1>
          <p class="page-subtitle">
            Сохранённые проекты из main_app. Кнопка «Извлечь» создаёт public-компоновку.
          </p>
        </div>
        <div class="row">
          <button class="btn" onClick={() => refetch()}>
            <Icon name="refresh-cw" />Обновить
          </button>
        </div>
      </div>

      <Show when={toast()}>
        <div
          class="card"
          style={{
            padding: 'var(--sp-3) var(--sp-4)',
            'margin-bottom': 'var(--sp-4)',
            color: toast()!.kind === 'err' ? 'var(--status-err)' : 'var(--status-ok)',
          }}
        >
          {toast()!.text}
        </div>
      </Show>

      <Show when={items.loading}>
        <div class="card" style={{ padding: 'var(--sp-5)' }}>Загрузка…</div>
      </Show>

      <Show when={items.error}>
        <div class="card" style={{ padding: 'var(--sp-5)', color: 'var(--status-err)' }}>
          Ошибка: {String(items.error)}
        </div>
      </Show>

      <Show when={items() && items()!.length === 0 && !items.loading}>
        <div class="card" style={{ padding: 'var(--sp-7)', 'text-align': 'center' }}>
          <div style={{ 'font-size': 'var(--fs-md)', 'margin-bottom': 'var(--sp-2)' }}>
            Здесь пусто
          </div>
          <div class="page-subtitle" style={{ 'margin-bottom': 'var(--sp-4)' }}>
            Чтобы видеть свои main_app проекты, привяжите main_app аккаунт в Настройках.
          </div>
          <button class="btn btn-primary" onClick={() => navigate({ to: '/settings' })}>
            Перейти в Настройки
          </button>
        </div>
      </Show>

      <Show when={items() && items()!.length > 0}>
        <div class="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table class="table">
            <thead>
              <tr>
                <th>Название</th>
                <th>Комментарий</th>
                <th>Владелец</th>
                <th>Изменён</th>
                <th style={{ 'text-align': 'right' }}>Размер</th>
                <th style={{ width: '180px' }}></th>
              </tr>
            </thead>
            <tbody>
              <For each={items()}>
                {(s) => (
                  <tr>
                    <td><span class="mono">{s.name}</span></td>
                    <td>{s.comment}</td>
                    <td>{s.source_user_name}</td>
                    <td>{formatDate(s.mtime)}</td>
                    <td style={{ 'text-align': 'right' }}>{formatBytes(s.size_bytes)}</td>
                    <td style={{ 'text-align': 'right' }}>
                      <button
                        class="btn"
                        disabled={busyId() === s.state_id}
                        onClick={() => onExtract(s)}
                      >
                        {busyId() === s.state_id ? 'Извлекаем…' : 'Извлечь компоновку'}
                      </button>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Show>
    </div>
  )
}
